import { useEffect, useMemo, useRef, useState } from 'react'
import { track } from '../lib/analytics'
import { scheduleChangedMillis, scheduleSeenDayKey, updatedSetIds } from '../lib/scheduleUpdates'
import { useSeenWatermark } from './useSeenWatermark'
import type { Set as FestivalSet } from '../types'

const NO_TIMES: number[] = []

/**
 * Ids of the day's sets whose day, time or stage moved since the user last had
 * that day of the event's schedule open — or, for a day they haven't opened
 * since, since they first opened the schedule. Each day's watermark advances
 * as soon as it's shown, so its "since" is frozen for the rest of the mount:
 * chips stay while the user flips between days and are gone next visit.
 */
export function useScheduleUpdatedSets(
  eventId: string,
  day: string | null,
  uid: string | null,
  daySets: readonly FestivalSet[],
  setsLoading: boolean,
): ReadonlySet<string> {
  const times = useMemo(() => daySets.map(scheduleChangedMillis).filter(t => t > 0), [daySets])
  // With no times it's only ever written once: the first time the schedule opens.
  const firstOpen = useSeenWatermark({
    uid, docName: 'scheduleSeen', key: eventId, times: NO_TIMES, active: true, logTag: 'schedule-updated',
  })
  const dayKey = day ? scheduleSeenDayKey(eventId, day) : undefined
  const daySeen = useSeenWatermark({
    uid: dayKey ? uid : null, docName: 'scheduleSeen', key: dayKey, times, active: true,
    itemsLoading: setsLoading, logTag: 'schedule-updated',
  })
  const ready = uid !== null && dayKey !== undefined && !firstOpen.loading && !daySeen.loading

  // Baseline per day, captured the first render that day is ready — during
  // render, like useFrozenValue, so it belongs to the render that read it.
  const scope = `${uid}:${eventId}`
  const [frozen, setFrozen] = useState<{ scope: string; days: Record<string, number | null> }>({ scope, days: {} })
  let days = frozen.scope === scope ? frozen.days : {}
  if (ready && dayKey && !(dayKey in days)) {
    days = { ...days, [dayKey]: daySeen.lastSeen ?? firstOpen.lastSeen }
    setFrozen({ scope, days })
  } else if (frozen.scope !== scope) {
    setFrozen({ scope, days })
  }
  const baseline = dayKey && dayKey in days ? days[dayKey] : null
  const ids = useMemo(() => updatedSetIds(daySets, baseline), [daySets, baseline])

  const tracked = useRef(new Set<string>())
  useEffect(() => {
    const key = `${scope}:${dayKey}`
    if (ids.size === 0 || tracked.current.has(key)) return
    tracked.current.add(key)
    track('schedule_updated_chips_shown', { set_count: ids.size })
  }, [ids, scope, dayKey])

  return ids
}
